/**
 * Attribute Options Facade
 *
 * Provides a clean API for components to interact with attribute options state.
 * Handles caching, loading states, and automatic deduplication of requests.
 *
 * Usage:
 * ```typescript
 * private readonly optionsFacade = inject(AttributeOptionsFacade);
 *
 * // Load options (cached, won't refetch if already loaded)
 * this.optionsFacade.loadOptions(attributeUuid);
 *
 * // Get options as observable
 * const options$ = this.optionsFacade.selectOptions$(attributeUuid);
 *
 * // Get loading state as observable
 * const isLoading$ = this.optionsFacade.selectLoading$(attributeUuid);
 * ```
 */
import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';

import {
  loadCachedAttributeOptions,
  clearCachedAttributeOptions,
} from '../../../store/entities/attributes/attributes.actions';
import {
  selectCachedAttributeOptions,
  selectCachedAttributeOptionsLoading,
  selectCachedAttributeOptionsError,
  selectCachedAttributeOptionsLoaded,
} from '../../../store/entities/attributes/attributes.selectors';
import type { AttributeOption } from './attribute.model';

@Injectable({ providedIn: 'root' })
export class AttributeOptionsFacade {
  private readonly store = inject(Store);

  /**
   * Load options for an attribute.
   * Cached - won't refetch if already loaded.
   */
  loadOptions(attributeUuid: string): void {
    this.store.dispatch(loadCachedAttributeOptions({ attributeUuid }));
  }

  /**
   * Select options for an attribute as an observable.
   */
  selectOptions$(attributeUuid: string): Observable<AttributeOption[]> {
    return this.store.select(selectCachedAttributeOptions(attributeUuid));
  }

  /**
   * Select loading state for an attribute as an observable.
   */
  selectLoading$(attributeUuid: string): Observable<boolean> {
    return this.store.select(selectCachedAttributeOptionsLoading(attributeUuid));
  }

  /**
   * Select error state for an attribute as an observable.
   */
  selectError$(attributeUuid: string): Observable<string | null> {
    return this.store.select(selectCachedAttributeOptionsError(attributeUuid));
  }

  /**
   * Check if options are already loaded for an attribute.
   */
  selectLoaded$(attributeUuid: string): Observable<boolean> {
    return this.store.select(selectCachedAttributeOptionsLoaded(attributeUuid));
  }

  /**
   * Clear all cached options.
   * Call on logout or domain switch.
   */
  clearAll(): void {
    this.store.dispatch(clearCachedAttributeOptions());
  }
}
